import { useCallback, useState } from 'react'
import { useFeatureFlags } from '@fs/zion-flags'
import { generateSls, saveSlsAndUpdateState, useSlsDispatchContext, useSlsStateContext } from '@fs/zion-sls'
import { getNewGroupDispatches } from '../utils/newGroup'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { headers, R2AppName } from '../../../../../shared/services/config'
import { errorCollector } from '../../../../../shared/utils/utils'

/**
 * @typedef {Object} UseNewGroupSaveResult
 * @property {(result: import('../../panels/addExistingPersonPanel/useAddExistingPersonFlow').AddExistingPersonResult) => Promise<void>} createGroup
 *   Gathers the chosen people into a new group. Resolves once SLS has answered, saved or errored,
 *   so the caller can hold its surfaces open until then.
 * @property {?Error} error - The last save failure, for the caller to surface.
 * @property {() => void} clearError - Drops that failure. The card outlives any one attempt, so
 *   without this the alert has no way off the screen.
 */

/**
 * Owns the write that creates a new group.
 *
 * @returns {UseNewGroupSaveResult}
 */
const useNewGroupSave = () => {
  const { images, currentImageArk } = useSlsStateContext()
  const slsDispatch = useSlsDispatchContext()
  const { userId } = useR2StateContext()
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])

  const [error, setError] = useState(null)

  const clearError = useCallback(() => setError(null), [])

  const createGroup = useCallback(
    async ({ personIds = [], primaryPersonIds = [] } = {}) => {
      setError(null)

      try {
        const dispatches = await getNewGroupDispatches({
          personIds,
          primaryPersonIds,
          images,
          currentPartitionKey: currentImageArk,
        })

        if (dispatches.length === 0) return

        await saveSlsAndUpdateState({
          sls: generateSls(dispatches),
          dispatches,
          saveChangesFlag,
          userId,
          dispatch: slsDispatch,
          appName: R2AppName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          errorHandler: (err) => {
            setError(err)
            errorCollector(err, null, {
              codeId: 94,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          },
          successHandler: () => {
            // Local state still needs the dispatches when the save flag is off.
            if (!saveChangesFlag.isOn) {
              slsDispatch({ type: 'BATCH_DISPATCHES', value: dispatches })
            }
          },
        })
      } catch (err) {
        setError(err)
        errorCollector(err, null, { codeId: 94, message: 'New group creation error' })
      }
    },
    [images, currentImageArk, saveChangesFlag, userId, slsDispatch]
  )

  return { createGroup, error, clearError }
}

export default useNewGroupSave
